import React, { useState, useRef, useEffect } from 'react';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import './WhatsNewDropdown.css';
import { EXTENSION_URL } from '../config/extension';
import { MCP_PAGE_TITLE, MCP_DOCS_PATH } from '../config/mcp';

// Stacked green-up / red-down caret — the table's watched-signal marker until
// issue #411 replaced it with a strategy badge; kept as this announcement's icon
// (announcement.icon renders any node, not just emoji).
const WatchCaretIcon = () => (
  <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 0.6, fontSize: '20px' }}>
    <FaCaretUp style={{ color: '#16a34a', display: 'block' }} />
    <FaCaretDown style={{ color: '#dc2626', display: 'block' }} />
  </span>
);

// Configuration for announcements - add new features here
const ANNOUNCEMENTS = [
  {
    id: 'mcp-smart-lists',
    type: 'new',
    // The title IS /mcp's — label and destination must agree (issue #310).
    title: MCP_PAGE_TITLE,
    description: 'Your smart lists, your watchlist and today\'s market read now work inside ChatGPT, Claude, Cursor, VS Code and any MCP-capable assistant. Ask which lists hold NVDA, how your watchlist is doing against the market, what moved into the short-squeeze list overnight, or for the contrarian-value list with PEG and earnings dates. Free daily allowance; larger when signed in.',
    icon: '🤖',
    date: '2026-09-07',
    link: MCP_DOCS_PATH,
    linkText: 'Connect'
  },
  {
    id: 'earnings-call-highlights',
    type: 'new',
    title: 'Earnings Call Highlights',
    description: 'The call is where the quarter actually gets explained — the partnership that just closed, the product nobody is buying yet, the question management would not answer. We now transcribe the call audio ourselves the moment it is published, about two hours after the call ends and well before the written transcript appears anywhere. Expand any watchlist stock that has reported and you get the developments and named partners, product families and what was said about adoption, growth rates by segment, what management credits for driving growth, the competitive picture, the threats to that growth ranked by how much is actually at risk, and the analyst Q&A with who asked what and where the answer dodged. Pro accounts only.',
    icon: '🎙️',
    date: '2026-08-27'
  },
  {
    id: 'expectations-bar',
    type: 'new',
    title: 'Expectations Bar',
    description: 'A beat is only half the story — what decides the reaction is how much was already priced in. The new Expectations Bar column scores each stock 0 to 100 on how high a bar it has to clear going into its print, blending where it sits in its 52-week range, how options are positioned, its sector’s sentiment and the market-wide tape. Sort ascending to surface washed-out names nobody expects anything from, descending to see what’s crowded. It tells you how much is priced in — not which way the stock will go.',
    icon: '🎚️',
    date: '2026-08-11'
  },
  {
    id: 'sector-fear-greed',
    type: 'new',
    title: 'Fear & Greed by Sector',
    description: 'One market-wide sentiment number hides a lot — today Energy reads greed while Real Estate and Utilities sit in fear, a 36-point spread. The Fear & Greed panel now breaks sentiment down across all eleven sectors, scored from our own price and technical data. Click any sector to see the five components behind its score: trend, 52-week position, highs versus lows, volume participation and relative strength.',
    icon: '🌡️',
    date: '2026-08-11'
  },
  {
    id: 'browser-push-notifications',
    type: 'new',
    title: 'Browser Notifications',
    description: 'Get alerts on your device even when QuantApe is closed: a morning digest of your watchlist stocks reporting earnings today plus high-impact economic events (CPI, FOMC), band-signal entries and exits as they trigger, and a combined heads-up 30 minutes before the close with the session’s signals and after-hours reporters. Enable it from the notifications menu — works on desktop and installed mobile app.',
    icon: '🔔',
    date: '2026-07-27'
  },
  {
    id: 'band-strategy-alerts',
    type: 'new',
    title: 'Band-Strategy Alerts + Webhooks',
    description: 'The Trading Range price chart now auto-fits a Bollinger band strategy to each stock—band→band, band→mean, or short→mean—showing how it actually trades and where the entries and exits land. Like a setup? Watch it, and get alerted the moment it triggers, delivered in real time to your webhook (Discord, Slack, or your own trading script).',
    icon: <WatchCaretIcon />,
    date: '2026-07-16'
  },
  {
    id: 'mobile-earnings-tape',
    type: 'new',
    title: 'Mobile Earnings Calendar',
    description: 'Scan every company reporting earnings this week and next. Each tile surfaces what matters at a glance—technical signals, after-hours moves, call/put options flow, fresh news—so you can spot what is worth digging into without opening a chart.',
    icon: '📅',
    date: '2026-05-07'
  },
  {
    id: 'ai-portfolios-preview',
    type: 'new',
    title: 'AI Managed Portfolios (Preview)',
    description: 'Browse AI-managed portfolios with different strategies—momentum, value, growth, and more. Follow any public portfolio to track its performance and holdings in real time.',
    icon: '🤖',
    date: '2026-02-28'
  },
  {
    id: 'business-model-rotation',
    type: 'new',
    title: 'Business Model Rotation',
    description: 'Track momentum by how companies make money—subscription, transaction, advertising, and more. See which revenue models are leading or lagging across daily, weekly, monthly, and quarterly timeframes.',
    icon: '📊',
    date: '2026-02-04'
  },
  {
    id: 'chrome-extension-v1',
    type: 'new',
    title: 'Chrome Extension',
    description: 'Spot stock tickers on any webpage. View fundamentals, ratings, and risk factors without leaving the page.',
    link: EXTENSION_URL,
    linkText: 'Get it free',
    icon: '🧩',
    date: '2025-02-03'
  }
];

const SEEN_KEY = 'whatsNewSeen';

const WhatsNewDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Check localStorage for seen announcements
    const seenIds = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
    const unread = ANNOUNCEMENTS.some(a => !seenIds.includes(a.id));
    setHasUnread(unread);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);

    // Mark all as seen when opening
    if (!isOpen) {
      const allIds = ANNOUNCEMENTS.map(a => a.id);
      localStorage.setItem(SEEN_KEY, JSON.stringify(allIds));
      setHasUnread(false);

      // Track open event
      if (window.amplitude) {
        window.amplitude.track('Whats New Opened');
      }
    }
  };

  const handleLinkClick = (announcement) => {
    if (window.amplitude) {
      window.amplitude.track('Whats New Click', {
        announcementId: announcement.id,
        announcementTitle: announcement.title
      });
    }
  };

  return (
    <div className="whats-new-dropdown" ref={dropdownRef}>
      <button className="whats-new-button" onClick={handleToggle}>
        <span>What's New</span>
        {hasUnread && <span className="unread-dot" />}
      </button>

      {isOpen && (
        <div className="whats-new-menu">
          <div className="whats-new-header">
            <span>What's New</span>
          </div>
          <div className="whats-new-list">
            {ANNOUNCEMENTS.map(announcement => (
              <div key={announcement.id} className={`whats-new-item ${announcement.type}`}>
                <div className="item-icon">{announcement.icon}</div>
                <div className="item-content">
                  <div className="item-header">
                    <span className={`item-badge ${announcement.type}`}>
                      {announcement.type === 'new' ? 'NEW' : announcement.type.toUpperCase()}
                    </span>
                    <span className="item-title">{announcement.title}</span>
                  </div>
                  <p className="item-description">{announcement.description}</p>
                  {announcement.link && (
                    <a
                      href={announcement.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="item-link"
                      onClick={() => handleLinkClick(announcement)}
                    >
                      {announcement.linkText || 'Learn more'} →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsNewDropdown;
